import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { saveSupportAccessToken } from '@/lib/support-access';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';

const codeFromHash = (): string | null => {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  return new URLSearchParams(hash).get('code');
};

export const SupportAccessPage = () => {
  const { t } = useTranslation('auth');
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const restoreAuth = useAuthStore((state) => state.restore);
  const openSupportSession = useChurchStore((state) => state.openSupportSession);
  const [status, setStatus] = useState<'working' | 'failed'>('working');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = codeFromHash();
    if (!code) {
      setStatus('failed');
      setError(tErrors('SUPPORT_ACCESS_LINK_INVALID'));
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const redeemed = await api.post<{
          accessToken: string;
          churchId: string;
          expiresAt: string;
        }>('/support-access/redeem', { code }, { withoutChurch: true });
        if (cancelled) return;
        saveSupportAccessToken(redeemed.accessToken);
        await restoreAuth();
        await openSupportSession(redeemed.churchId);
        if (!cancelled) navigate('/home', { replace: true });
      } catch (err) {
        if (cancelled) return;
        setStatus('failed');
        setError(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, openSupportSession, restoreAuth, tErrors]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface-sunken px-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 text-center">
        {status === 'working' ? (
          <>
            <Spinner className="mx-auto size-6 text-brand" />
            <p className="mt-4 text-body text-fg">{t('supportAccess.opening')}</p>
          </>
        ) : (
          <>
            <h1 className="text-page-title font-semibold text-fg">{t('supportAccess.failedTitle')}</h1>
            <p className="mt-2 text-body-sm text-fg-secondary">{error}</p>
            <Button className="mt-6" variant="outline" onClick={() => window.close()}>
              {t('supportAccess.close')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
